import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminService } from '@/services/admin.service';
import { Save, Settings, CheckCircle } from 'lucide-react';

interface QuestionTypeTemplate {
  type_id: string;
  type_name: string;
  description: string;
  enabled: boolean;
  prompt_template: string;
  validation_rules: {
    min_options?: number;
    max_options?: number;
    require_explanation: boolean;
    min_explanation_words?: number;
  };
  scoring_config: {
    points: number;
    partial_credit_allowed: boolean;
  };
}

export function QuestionTypeConfig() {
  const queryClient = useQueryClient();
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const [templates, setTemplates] = useState<QuestionTypeTemplate[]>([
    {
      type_id: 'MCQ',
      type_name: 'Multiple Choice Question',
      description: 'Standard multiple choice with 4 options (A, B, C, D)',
      enabled: true,
      prompt_template: `Generate a multiple choice question based on the following context:

Context: {context}

Requirements:
- Topic: {topic}
- Difficulty: {difficulty}
- Create exactly 4 options (A, B, C, D)
- Only one correct answer
- Include detailed explanation
- Align with exam blueprint`,
      validation_rules: {
        min_options: 4,
        max_options: 4,
        require_explanation: true,
        min_explanation_words: 30,
      },
      scoring_config: {
        points: 1,
        partial_credit_allowed: false,
      },
    },
    {
      type_id: 'decision_tree',
      type_name: 'Decision Tree',
      description: 'Multi-step decision-making question',
      enabled: true,
      prompt_template: `Generate a decision tree question based on the following context:

Context: {context}

Requirements:
- Topic: {topic}
- Difficulty: {difficulty}
- Create a scenario requiring multiple decision points
- Provide clear branching logic
- Include detailed explanation of decision path`,
      validation_rules: {
        require_explanation: true,
        min_explanation_words: 50,
      },
      scoring_config: {
        points: 2,
        partial_credit_allowed: true,
      },
    },
    {
      type_id: 'case_study',
      type_name: 'Case Study',
      description: 'Comprehensive scenario-based question',
      enabled: true,
      prompt_template: `Generate a case study question based on the following context:

Context: {context}

Requirements:
- Topic: {topic}
- Difficulty: {difficulty}
- Create realistic business scenario
- Include relevant financial data
- Provide comprehensive analysis in explanation`,
      validation_rules: {
        require_explanation: true,
        min_explanation_words: 100,
      },
      scoring_config: {
        points: 3,
        partial_credit_allowed: true,
      },
    },
    {
      type_id: 'true_false',
      type_name: 'True/False',
      description: 'Simple binary choice question',
      enabled: false,
      prompt_template: `Generate a true/false question based on the following context:

Context: {context}

Requirements:
- Topic: {topic}
- Difficulty: {difficulty}
- Clear unambiguous statement
- Include explanation of why the answer is true or false`,
      validation_rules: {
        min_options: 2,
        max_options: 2,
        require_explanation: true,
        min_explanation_words: 20,
      },
      scoring_config: {
        points: 1,
        partial_credit_allowed: false,
      },
    },
  ]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const token = localStorage.getItem('accessToken') || '';
      return await adminService.updateQuestionTypeConfig(templates, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['question-type-config'] });
      setHasChanges(false);
    },
  });

  const handleToggleEnabled = (typeId: string) => {
    setTemplates(templates.map(t =>
      t.type_id === typeId ? { ...t, enabled: !t.enabled } : t
    ));
    setHasChanges(true);
  };

  const handleUpdateTemplate = (typeId: string, field: string, value: any) => {
    setTemplates(templates.map(t =>
      t.type_id === typeId ? { ...t, [field]: value } : t
    ));
    setHasChanges(true);
  };

  const handleUpdateValidation = (typeId: string, field: string, value: any) => {
    setTemplates(templates.map(t =>
      t.type_id === typeId
        ? { ...t, validation_rules: { ...t.validation_rules, [field]: value } }
        : t
    ));
    setHasChanges(true);
  };

  const handleUpdateScoring = (typeId: string, field: string, value: any) => {
    setTemplates(templates.map(t =>
      t.type_id === typeId
        ? { ...t, scoring_config: { ...t.scoring_config, [field]: value } }
        : t
    ));
    setHasChanges(true);
  };

  const handleSave = () => {
    saveMutation.mutate();
  };

  const selectedTemplate = templates.find(t => t.type_id === selectedType);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Question Type Configuration</h1>
          <p className="text-gray-600 mt-1">Configure templates and rules for question generation</p>
        </div>
        <button
          onClick={handleSave}
          disabled={!hasChanges || saveMutation.isPending}
          className="btn-primary flex items-center"
        >
          <Save className="w-4 h-4 mr-2" />
          {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Save Reminder */}
      {hasChanges && (
        <div className="card bg-yellow-50 border-yellow-200">
          <div className="flex items-center space-x-3">
            <Settings className="w-5 h-5 text-yellow-600" />
            <p className="text-sm text-yellow-900">
              You have unsaved changes. Click "Save Changes" to apply your configuration.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Question Types List */}
        <div className="lg:col-span-1">
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Question Types</h2>
            <div className="space-y-2">
              {templates.map((template) => (
                <button
                  key={template.type_id}
                  onClick={() => setSelectedType(template.type_id)}
                  className={`w-full text-left p-4 rounded-lg transition-colors ${
                    selectedType === template.type_id
                      ? 'bg-primary-50 border-2 border-primary-500'
                      : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{template.type_name}</h3>
                        {template.enabled && (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        )}
                      </div>
                      <p className="text-xs text-gray-600">{template.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Configuration Panel */}
        <div className="lg:col-span-2">
          {selectedTemplate ? (
            <div className="card space-y-6">
              {/* Basic Settings */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Settings</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type Name
                    </label>
                    <input
                      type="text"
                      value={selectedTemplate.type_name}
                      onChange={(e) => handleUpdateTemplate(selectedTemplate.type_id, 'type_name', e.target.value)}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      rows={2}
                      value={selectedTemplate.description}
                      onChange={(e) => handleUpdateTemplate(selectedTemplate.type_id, 'description', e.target.value)}
                      className="input-field"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedTemplate.enabled}
                      onChange={() => handleToggleEnabled(selectedTemplate.type_id)}
                      className="w-4 h-4 text-primary-600"
                    />
                    <label className="ml-2 text-sm text-gray-700">
                      Enable this question type for generation
                    </label>
                  </div>
                </div>
              </div>

              {/* Prompt Template */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Prompt Template</h2>
                <textarea
                  rows={12}
                  value={selectedTemplate.prompt_template}
                  onChange={(e) => handleUpdateTemplate(selectedTemplate.type_id, 'prompt_template', e.target.value)}
                  className="input-field font-mono text-sm"
                  placeholder="Enter prompt template..."
                />
                <p className="text-xs text-gray-500 mt-2">
                  Available variables: {'{context}'}, {'{topic}'}, {'{difficulty}'}
                </p>
              </div>

              {/* Validation Rules */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Validation Rules</h2>
                <div className="grid grid-cols-2 gap-4">
                  {selectedTemplate.validation_rules.min_options !== undefined && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Min Options
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={selectedTemplate.validation_rules.min_options}
                        onChange={(e) => handleUpdateValidation(selectedTemplate.type_id, 'min_options', parseInt(e.target.value))}
                        className="input-field"
                      />
                    </div>
                  )}

                  {selectedTemplate.validation_rules.max_options !== undefined && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max Options
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={selectedTemplate.validation_rules.max_options}
                        onChange={(e) => handleUpdateValidation(selectedTemplate.type_id, 'max_options', parseInt(e.target.value))}
                        className="input-field"
                      />
                    </div>
                  )}

                  {selectedTemplate.validation_rules.min_explanation_words !== undefined && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Min Explanation Words
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={selectedTemplate.validation_rules.min_explanation_words}
                        onChange={(e) => handleUpdateValidation(selectedTemplate.type_id, 'min_explanation_words', parseInt(e.target.value))}
                        className="input-field"
                      />
                    </div>
                  )}

                  <div className="col-span-2">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedTemplate.validation_rules.require_explanation}
                        onChange={(e) => handleUpdateValidation(selectedTemplate.type_id, 'require_explanation', e.target.checked)}
                        className="w-4 h-4 text-primary-600"
                      />
                      <label className="ml-2 text-sm text-gray-700">
                        Require explanation
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Scoring Configuration */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Scoring Configuration</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Points
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={selectedTemplate.scoring_config.points}
                      onChange={(e) => handleUpdateScoring(selectedTemplate.type_id, 'points', parseFloat(e.target.value))}
                      className="input-field"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedTemplate.scoring_config.partial_credit_allowed}
                      onChange={(e) => handleUpdateScoring(selectedTemplate.type_id, 'partial_credit_allowed', e.target.checked)}
                      className="w-4 h-4 text-primary-600"
                    />
                    <label className="ml-2 text-sm text-gray-700">
                      Allow partial credit
                    </label>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="py-12 text-center">
                <Settings className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Type Selected</h3>
                <p className="text-gray-600">Select a question type from the list to configure</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <p className="text-sm text-gray-600 mb-1">Total Types</p>
          <p className="text-2xl font-bold text-gray-900">{templates.length}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-600 mb-1">Enabled Types</p>
          <p className="text-2xl font-bold text-green-600">
            {templates.filter(t => t.enabled).length}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-600 mb-1">Disabled Types</p>
          <p className="text-2xl font-bold text-gray-600">
            {templates.filter(t => !t.enabled).length}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-600 mb-1">Avg Points</p>
          <p className="text-2xl font-bold text-primary-600">
            {(templates.reduce((sum, t) => sum + t.scoring_config.points, 0) / templates.length).toFixed(1)}
          </p>
        </div>
      </div>
    </div>
  );
}
